import { Component, Inject, Input, PLATFORM_ID, type OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { type SSMProcessData } from '../../models/start-service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../../services/storage.service';
import { StorageEnum } from '../../enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../../services/cms.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../../services/util.service';
import { format } from 'date-fns';
import { AccountDetail } from '../../models/account-detail';
import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-ssm-side-panel',
  imports: [MatCardModule, MatTooltipModule, CurrencyPipe],
  templateUrl: './ssm-side-panel.component.html',
  styleUrl: './ssm-side-panel.component.scss',
})
export class SsmSidePanelComponent implements OnInit {
  ssmData?: SSMProcessData;
  mailingAddressSame: boolean;
  pageLabels: any;
  pageReady = false;
  @Input() panelData: any;
  @Input() step!: number;
  accountDetail: AccountDetail = new AccountDetail();
  isBrowser: boolean;

  constructor(
    private readonly storage: StorageService,
    private readonly cms: CmsService,
    private readonly utils: UtilService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.ssmData = this.storage.getSession(StorageEnum.SSMData);
    this.ssmData?.UseServiceAddressAsMailingAddress !== undefined
      ? (this.mailingAddressSame = this.ssmData.UseServiceAddressAsMailingAddress)
      : (this.mailingAddressSame = false);
    if (this.isBrowser) {
      this.accountDetail = this.storage.getSession(StorageEnum.AccountDetail);
    }
  }

  ngOnInit(): void {
    if (this.panelData !== null && this.panelData !== undefined) {
      this.cms.getContent('oas-ssm-side-panel', 'Side Panel').subscribe((data: any) => {
        this.pageLabels = data;
        this.pageReady = true;
      });
    }
  }

  showAddressSection(): boolean {
    if (
      ((this.panelData.CurrentProcess === 'start' && this.step >= 1) ||
        (this.panelData.CurrentProcess === 'move' && this.step >= 2) ||
        (this.panelData.CurrentProcess === 'reconnect' && this.step === 2)) &&
      !this.utils.isNullOrUndefined(this.panelData.StartRequest?.Address1)
    ) {
      return true;
    } else {
      return false;
    }
  }

  formatAddress(mailingAddressSame: boolean): string {
    if (!mailingAddressSame) {
      return (
        this.panelData.MailingAddress?.Address1 +
        '<br>' +
        this.panelData.MailingAddress?.City +
        ', ' +
        this.panelData.MailingAddress?.State +
        ' ' +
        this.panelData.MailingAddress?.PostalCode
      );
    } else {
      return (
        this.panelData.StartRequest?.Address1 +
        '<br>' +
        this.panelData.StartRequest?.City +
        ', ' +
        this.panelData.StartRequest?.State +
        ' ' +
        this.panelData.StartRequest?.ZipCode
      );
    }
  }

  showDateSection(): boolean {
    if (
      (!this.utils.isNullOrUndefined(this.panelData.FirstAvailable) ||
        !this.utils.isNullOrUndefined(this.panelData.Date)) &&
      ((this.panelData.CurrentProcess === 'start' && this.step > 2) ||
        (this.panelData.CurrentProcess === 'move' && this.step > 3) ||
        (this.panelData.CurrentProcess === 'reconnect' && this.step > 1))
    ) {
      return true;
    } else {
      return false;
    }
  }

  showServiceType(): boolean {
    if (!this.utils.isNullOrUndefined(this.panelData.StartRequest?.StartServiceType)) {
      return true;
    } else {
      return false;
    }
  }

  serviceTypeText(): string {
    if (this.panelData.StartRequest?.StartServiceType === 'MKSW') {
      return this.pageLabels.switchProviderText;
    } else {
      return this.pageLabels.turnOnText;
    }
  }

  showEstimatedDateText(): boolean {
    return (
      this.panelData.CurrentProcess === 'start' &&
      this.panelData.StartRequest.StartServiceType === 'MKSW' &&
      this.step > 2
    );
  }

  getEstimatedSwitchStartDateText(): string {
    // for market switch, we show an estimated start date that is calculated as follows
    // if today is before or on the 19th of the month we show the first day of next month, if it's 20th or after we show first day of two months from now
    const today = new Date();
    if (today.getDate() <= 19) {
      return format(today.setMonth(today.getMonth() + 1, 1), 'MMM do, yyyy');
    } else {
      return format(today.setMonth(today.getMonth() + 2, 1), 'MMM do, yyyy');
    }
  }

  getDateLabel(): string {
    if (this.panelData.StartRequest?.StartServiceType === 'MKSW') {
      return this.pageLabels.estimatedDateText;
    } else {
      if (this.panelData.CurrentProcess === 'reconnect') {
        return this.pageLabels.reconnectDateAndTimeText;
      } else {
        return this.pageLabels.turnOnDateText;
      }
    }
  }

  getDateText(): string {
    if (this.panelData.StartRequest?.StartServiceType === 'MKSW') {
      // if today is between 1st and 19th of month, set this date to first of next month, else set it to first day of two months from now
      const today = new Date();
      const startMonth = new Date();
      if (today.getDate() <= 19) {
        startMonth.setMonth(startMonth.getMonth() + 1);
      } else {
        startMonth.setMonth(startMonth.getMonth() + 2);
      }
      return format(startMonth, 'MMM do, yyyy');
    } else {
      if (this.panelData.FirstAvailable) {
        return this.pageLabels.firstAvailableText;
      } else {
        let returnDate = '';
        if (this.panelData.Date !== undefined) {
          returnDate = this.panelData.Date;
        }
        const returnTime =
          this.panelData.Time === 'morning' ? this.pageLabels.morningText : this.pageLabels.afternoonText;
        return format(returnDate, 'MMM do, yyyy') + '<br>' + returnTime;
      }
    }
  }

  showRateSection(): boolean {
    if (
      !this.utils.isNullOrUndefined(this.panelData.PricePerTherm) &&
      ((this.panelData.CurrentProcess === 'start' && this.step === 3) ||
        (this.panelData.CurrentProcess === 'move' && this.step === 4) ||
        (this.panelData.CurrentProcess === 'reconnect' && this.step === 2))
    ) {
      return true;
    } else {
      return false;
    }
  }

  showCustomerServiceFee(): boolean {
    if (
      this.showRateSection() &&
      !this.utils.isNullOrUndefined(this.panelData.CustomerServiceFee) &&
      !this.utils.isNullOrUndefined(this.panelData.PackageIsOPP)
    ) {
      if (this.panelData.PackageIsOPP) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  getRateDetailsText(): string {
    return this.panelData.PlanType + '<br>' + this.panelData.ProductTerm + '<br>' + this.panelData.PricePerTherm;
  }

  getStopAddress(): string {
    return this.panelData.StopServiceAddress;
  }

  showStopAddress(): boolean {
    return this.panelData.CurrentProcess === 'move' && this.step > 1;
  }

  getStopDate(): string {
    return this.panelData.StopRequest.FirstAvailableText === 'First Available'
      ? this.panelData.StopRequest.FirstAvailableText
      : format(this.panelData.StopRequest.StopDate, 'MMMM do, yyyy');
  }

  showStopDAte(): boolean {
    return this.panelData.CurrentProcess === 'move' && this.step > 1;
  }
}
